import { Handle, Position } from "@xyflow/react";

export type PersonalDiagramNodeData = {
  authorText?: string;
  /** Resolved display lines for bound field values (already formatted). */
  valueLines?: string[];
  emptyPlaceholder?: string;
  fieldKeys?: string[];
  /** When true, show selection ring (author editor). */
  selected?: boolean;
  /** editor = selectable/authoring; readonly = engagement presentation */
  variant?: "editor" | "readonly";
  /** Soft fill color key — same palettes in editor and readonly */
  colorIndex?: number;
};

const NODE_PALETTES = [
  {
    bg: "bg-teal-50",
    border: "border-teal-200",
    label: "text-teal-700",
    accent: "bg-teal-500",
  },
  {
    bg: "bg-sky-50",
    border: "border-sky-200",
    label: "text-sky-700",
    accent: "bg-sky-500",
  },
  {
    bg: "bg-violet-50",
    border: "border-violet-200",
    label: "text-violet-700",
    accent: "bg-violet-500",
  },
  {
    bg: "bg-amber-50",
    border: "border-amber-200",
    label: "text-amber-800",
    accent: "bg-amber-500",
  },
  {
    bg: "bg-rose-50",
    border: "border-rose-200",
    label: "text-rose-700",
    accent: "bg-rose-500",
  },
  {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    label: "text-emerald-700",
    accent: "bg-emerald-500",
  },
];

/**
 * Uniform rounded box for personal_diagram — author text on top, bound values below.
 * Same look in editor and readonly; editor adds a selection ring when selected.
 */
export default function PersonalDiagramNode({
  data,
  selected,
}: {
  data: PersonalDiagramNodeData;
  selected?: boolean;
}) {
  const authorText = data.authorText?.trim() || "";
  const valueLines = Array.isArray(data.valueLines) ? data.valueLines : [];
  const placeholder = data.emptyPlaceholder ?? "—";
  const hasValues = valueLines.length > 0;
  const isSelected = selected || data.selected;
  const isEditor = data.variant === "editor";
  const palette =
    NODE_PALETTES[(data.colorIndex ?? 0) % NODE_PALETTES.length];

  return (
    <div
      className={`min-w-[200px] max-w-[340px] rounded-xl border-2 ${palette.bg} ${palette.border} px-4 py-3.5 shadow-sm ${
        isEditor && isSelected
          ? "ring-2 ring-primary/40 ring-offset-1 shadow-md"
          : ""
      }`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-slate-400 !border-2 !border-white !-top-1.5"
      />

      {authorText ? (
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`inline-block h-2 w-2 rounded-full shrink-0 ${palette.accent}`}
          />
          <div
            className={`text-[13px] font-semibold tracking-wide uppercase ${palette.label} leading-tight`}
          >
            {authorText}
          </div>
        </div>
      ) : null}

      {hasValues ? (
        <div className="space-y-1.5">
          {valueLines.map((line, idx) => (
            <div
              key={idx}
              className="text-[17px] font-medium text-slate-900 whitespace-pre-wrap break-words leading-snug"
            >
              {line || (
                <span className="text-slate-400 italic font-normal">
                  {placeholder}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[17px] text-slate-400 italic">{placeholder}</div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-slate-400 !border-2 !border-white !-bottom-1.5"
      />
    </div>
  );
}
